import React, { useMemo } from 'react';
import dayjs from 'dayjs';
import isToday from 'dayjs/plugin/isToday';
import isTomorrow from 'dayjs/plugin/isTomorrow';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Typography,
    Divider,
    Box,
    Checkbox,
    Button,
    DialogActions
} from '@mui/material';

dayjs.extend(isToday);
dayjs.extend(isTomorrow);

function formatDayLabel(dateObj) {
    if (dateObj.isToday()) return 'Today';
    if (dateObj.isTomorrow()) return 'Tomorrow';

    const diff = dateObj.diff(dayjs(), 'day');
    if (diff < 0) return `${Math.abs(diff)} day${Math.abs(diff) > 1 ? 's' : ''} ago`;
    if (diff > 1) return `In ${diff} days`;

    return dateObj.format('dddd, MMM D');
}

function formatScheduledTime(todo) {
    if (todo.schedule_all_day) return 'Any Time';
    const begin = todo.schedule_begin_time_selected ? (todo.schedule_begin_time === "12:00 PM" ? "Noon" : todo.schedule_begin_time) : '';
    const end = todo.schedule_end_time_selected ? (todo.schedule_end_time === "12:00 PM" ? "Noon" : todo.schedule_end_time) : '';
    return begin && end ? `${begin} - ${end}` : begin || end || '';
}

function ToDoMaster({ isOpen, onClose, toDoItems = [] }) {

    const groupedByDate = useMemo(() => {
        const groups = {};

        toDoItems.forEach(todo => {
            const parsedDate = dayjs(todo.schedule_date, ['YYYY-MM-DD', 'MM/DD/YYYY'], true);
            const dateKey = parsedDate.isValid() ? parsedDate.format('YYYY-MM-DD') : 'invalid-date';

            if (!groups[dateKey]) groups[dateKey] = [];
            groups[dateKey].push(todo);
        });

        // Sort items within each group
        Object.keys(groups).forEach(dateKey => {
            groups[dateKey].sort((a, b) => {
                // All-day items come first
                if (a.schedule_all_day && !b.schedule_all_day) return -1;
                if (!a.schedule_all_day && b.schedule_all_day) return 1;

                // If neither are all-day, compare begin times
                const aTime = dayjs(a.schedule_begin_time, 'h:mm A');
                const bTime = dayjs(b.schedule_begin_time, 'h:mm A');

                return aTime.isAfter(bTime) ? 1 : -1;
            });
        });

        return groups;
    }, [toDoItems]);

    const sortedDates = Object.keys(groupedByDate).sort();

    return (
        <Dialog open={isOpen} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>📝 My To-Do List</DialogTitle>
            <DialogContent dividers>
                {sortedDates.length === 0 ? (
                    <Typography>No To-Do items assigned.</Typography>
                ) : (
                    sortedDates.map(date => {
                        const dateObj = dayjs(date, ['YYYY-MM-DD', 'MM/DD/YYYY'], true);

                        return (
                            <Box key={date} sx={{ mb: 3 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                    {formatDayLabel(dateObj)}
                                </Typography>
                                <Divider sx={{ mb: 1 }} />

                                {groupedByDate[date].map(todo => {
                                    const isPrivate = todo.private || todo.is_private;

                                    return (
                                        <Box
                                            key={todo.id}
                                            sx={{
                                                border: '1px solid #ddd',
                                                borderRadius: 2,
                                                p: 2,
                                                mb: 1,
                                                backgroundColor: isPrivate ? '#fce4ec' : '#e3f2fd',
                                            }}
                                        >
                                            {/* Time */}
                                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                                {formatScheduledTime(todo)}
                                            </Typography>

                                            {/* Title & Checkbox */}
                                            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                                                <Checkbox size="small" disabled checked={todo.status === 1} />
                                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                    {todo.title}
                                                </Typography>
                                            </Box>

                                            {/* Notes */}
                                            {todo.notes && (
                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                                    {todo.notes}
                                                </Typography>
                                            )}

                                            {/* Event title */}
                                            <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
                                                Event: {todo.event_title}
                                            </Typography>
                                        </Box>
                                    );
                                })}
                            </Box>
                        );
                    })
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} variant="outlined" color="warning">
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default ToDoMaster;
